import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import {
    CellPlacement,
    DatasetRow,
    GridRowSpec,
    ImageLayer,
    Layout,
    PaneContent,
    PaneSpec,
    PanelKind,
    Plane,
    planeContent,
    PRIMARY_ROW_INDEX,
    RowTemplate,
    ViewportCell,
    ViewportId,
    ViewportIdFactory,
} from './types';

/** Distributes 100% evenly across `count` items. */
export function evenPercents(count: number): number[] {
    if (count <= 0) {
        return [];
    }
    const share = 100 / count;
    return Array.from({ length: count }, () => share);
}

/** Places a pane in a single track, the common 1x1 case. */
export function at(colStart: number, rowStart: number): CellPlacement {
    return { colStart, colSpan: 1, rowStart, rowSpan: 1 };
}

/**
 * The template for a row of evenly-sized planes side by side: one column
 * track each, one row track. The shape the grid has always hung.
 */
export function evenPlanes(
    planes: ReadonlyArray<Plane>,
    layer: ImageLayer = ImageLayer.Blended
): RowTemplate {
    return {
        columnPercents: evenPercents(planes.length),
        rowPercents: [100],
        panes: planes.map((plane, index) => ({
            content: planeContent(plane, layer),
            placement: at(index + 1, 1),
        })),
    };
}

/**
 * The tracks and freshly minted image panes a template hangs in a row. A
 * template describes the image panes only; a panel pane in it is ignored,
 * since panels hang in the row's band.
 */
export function hangTemplate(
    template: RowTemplate,
    createId: ViewportIdFactory
): Pick<DatasetRow, 'columnPercents' | 'rowPercents' | 'cells'> {
    return {
        columnPercents: [...template.columnPercents],
        rowPercents: [...template.rowPercents],
        cells: template.panes
            .filter((pane) => pane.content.kind === 'plane')
            .map(
                (pane): ViewportCell => ({
                    id: createId(),
                    content: pane.content,
                    placement: pane.placement,
                })
            ),
    };
}

/**
 * A row's shape as a template: its tracks and image panes, without dataset
 * or ids. The band of panels is not part of the shape.
 */
export function templateOfRow(row: DatasetRow): RowTemplate {
    return {
        columnPercents: [...row.columnPercents],
        rowPercents: [...row.rowPercents],
        panes: imageCellsOf(row).map((cell) => ({
            content: cell.content,
            placement: cell.placement,
        })),
    };
}

/**
 * Builds a layout from an ordered list of row specs. Rows are sized evenly
 * within the grid; each row's own tracks come from its template. Ids are
 * minted by the injected factory so the result is deterministic under test.
 */
export function buildLayout(
    rows: ReadonlyArray<GridRowSpec>,
    createId: ViewportIdFactory
): Layout {
    const heights = evenPercents(rows.length);
    return {
        rows: rows.map(
            (rowSpec, rowIndex): DatasetRow => ({
                dataset: rowSpec.dataset,
                heightPercent: heights[rowIndex],
                ...hangTemplate(rowSpec.template, createId),
            })
        ),
    };
}

function sameContent(a: PaneContent, b: PaneContent): boolean {
    if (a.kind === 'plane' && b.kind === 'plane') {
        return a.plane === b.plane && a.layer === b.layer;
    }
    return a.kind === b.kind;
}

function samePane(a: PaneSpec, b: PaneSpec): boolean {
    const p = a.placement;
    const q = b.placement;
    return (
        sameContent(a.content, b.content) &&
        p.colStart === q.colStart &&
        p.colSpan === q.colSpan &&
        p.rowStart === q.rowStart &&
        p.rowSpan === q.rowSpan
    );
}

/**
 * Whether two templates hang the same shape: the same number of tracks each
 * way, and the same panes in the same places showing the same content. Track
 * sizes do not count, so a row the user has resized still hangs its shape.
 */
export function sameShape(a: RowTemplate, b: RowTemplate): boolean {
    return (
        a.columnPercents.length === b.columnPercents.length &&
        a.rowPercents.length === b.rowPercents.length &&
        a.panes.length === b.panes.length &&
        a.panes.every((pane, index) => samePane(pane, b.panes[index]))
    );
}

/** The plane a pane draws, or `undefined` when it is not a plane pane. */
export function planeOfCell(cell: ViewportCell): Plane | undefined {
    return cell.content.kind === 'plane' ? cell.content.plane : undefined;
}

/** The row's first image pane: the one that stands for it. */
export function leadCellOf(row: DatasetRow): ViewportCell | undefined {
    return imageCellsOf(row)[0];
}

/** The images the bound rows of `layout` show, in row order, each once. */
export function imagesOfRows(layout: Layout): SeriesInstanceUid[] {
    const images: SeriesInstanceUid[] = [];
    for (const row of layout.rows) {
        const uid = row.dataset?.seriesInstanceUid;
        if (uid !== undefined && !images.includes(uid)) {
            images.push(uid);
        }
    }
    return images;
}

/**
 * The index of the first bound row showing this series, or `undefined`
 * when no row does. Padding on either UID does not count.
 */
export function rowIndexOfSeries(
    layout: Layout,
    seriesInstanceUid: SeriesInstanceUid
): number | undefined {
    const index = layout.rows.findIndex(
        (row) => row.dataset?.seriesInstanceUid === seriesInstanceUid
    );
    return index === -1 ? undefined : index;
}

/**
 * The row that hosts what belongs to a series: the first row showing it,
 * else the primary row, also when no series is named.
 */
export function hostRowIndex(
    layout: Layout,
    hostSeriesUid: SeriesInstanceUid | undefined
): number {
    return (
        (hostSeriesUid !== undefined
            ? rowIndexOfSeries(layout, hostSeriesUid)
            : undefined) ?? PRIMARY_ROW_INDEX
    );
}

/** Whether a pane shows a panel rather than a plane of the row's image. */
export function isPanelCell(cell: ViewportCell): boolean {
    return cell.content.kind !== 'plane';
}

/** The row's image panes, in reading order. */
export function imageCellsOf(row: DatasetRow): ViewportCell[] {
    return row.cells.filter((cell) => !isPanelCell(cell));
}

/** The row's band panels, in reading order; none without a band. */
export function panelCellsOf(row: DatasetRow): ViewportCell[] {
    return row.cells.filter(isPanelCell);
}

/** The kind of panel a pane shows, or `undefined` for an image pane. */
export function panelKindOfCell(cell: ViewportCell): PanelKind | undefined {
    return cell.content.kind === 'plane' ? undefined : cell.content.kind;
}

/** The side of the fused pair a pane draws, or `undefined` for a non-plane pane. */
function layerOfCell(cell: ViewportCell): ImageLayer | undefined {
    return cell.content.kind === 'plane' ? cell.content.layer : undefined;
}

/** The planes a row's panes draw, in reading order. */
export function planesOfRow(row: DatasetRow): Plane[] {
    return row.cells.flatMap((cell) =>
        cell.content.kind === 'plane' ? [cell.content.plane] : []
    );
}

/**
 * Whether rotating a row's planes changes what it draws: it shows at least
 * two distinct planes. A row of one plane, or a side-by-side pair drawing
 * one plane twice, has nothing to rotate.
 */
export function rowCanRotate(row: DatasetRow): boolean {
    return new Set(planesOfRow(row)).size > 1;
}

/**
 * Whether a row shows its fused pair apart: at least one pane draws one
 * side of the fusion rather than the blend.
 */
export function isSideBySide(row: DatasetRow): boolean {
    return row.cells.some((cell) => {
        const layer = layerOfCell(cell);
        return layer !== undefined && layer !== ImageLayer.Blended;
    });
}

/** Whether a pane's placement covers a given track intersection. */
function covers(cell: ViewportCell, column: number, subRow: number): boolean {
    const { colStart, colSpan, rowStart, rowSpan } = cell.placement;
    return (
        column >= colStart &&
        column < colStart + colSpan &&
        subRow >= rowStart &&
        subRow < rowStart + rowSpan
    );
}

/** Where a pane sits: its row, the row's index, and the pane itself. */
export interface LocatedViewport {
    readonly rowIndex: number;
    readonly row: DatasetRow;
    readonly cell: ViewportCell;
}

/** Finds the pane with this id, or `undefined` when no row holds it. */
export function locateViewport(
    layout: Layout,
    id: ViewportId
): LocatedViewport | undefined {
    for (let rowIndex = 0; rowIndex < layout.rows.length; rowIndex++) {
        const row = layout.rows[rowIndex];
        const cell = row.cells.find((candidate) => candidate.id === id);
        if (cell) {
            return { rowIndex, row, cell };
        }
    }
    return undefined;
}

/**
 * The viewport one step from `id` in the direction given, or `undefined` at
 * an edge. Navigation is geometric: it follows the track grid the panes are
 * drawn in, so focus moves to the pane the user sees in that direction even
 * where two panes share a column. A vertical step that leaves the image
 * panes enters the row's band of panels, at the band column under the
 * pane's own column or the band's last one; a step that leaves the row
 * crosses into the adjacent dataset row, into its band from below or its
 * first sub-row from above, at the same column.
 */
export function neighbourViewport(
    layout: Layout,
    id: ViewportId,
    rowDelta: number,
    columnDelta: number
): ViewportCell | undefined {
    const located = locateViewport(layout, id);
    if (!located) {
        return undefined;
    }
    const { rowIndex, row, cell } = located;
    const { colStart, colSpan, rowStart, rowSpan } = cell.placement;
    const inBand = isPanelCell(cell);
    const peers = inBand ? panelCellsOf(row) : imageCellsOf(row);

    if (columnDelta !== 0) {
        const targetColumn =
            columnDelta > 0 ? colStart + colSpan : colStart - 1;
        const columnCount = inBand
            ? (row.band?.columnPercents.length ?? 0)
            : row.columnPercents.length;
        if (targetColumn < 1 || targetColumn > columnCount) {
            return undefined;
        }
        return peers.find((candidate) =>
            covers(candidate, targetColumn, rowStart)
        );
    }

    if (rowDelta === 0) {
        return undefined;
    }
    if (!inBand) {
        const targetSubRow = rowDelta > 0 ? rowStart + rowSpan : rowStart - 1;
        if (targetSubRow >= 1 && targetSubRow <= row.rowPercents.length) {
            return peers.find((candidate) =>
                covers(candidate, colStart, targetSubRow)
            );
        }
        if (rowDelta > 0 && row.band) {
            return bandCellAt(row, colStart);
        }
    } else if (rowDelta < 0) {
        return imageCellsOf(row).find((candidate) =>
            covers(
                candidate,
                Math.min(colStart, row.columnPercents.length),
                row.rowPercents.length
            )
        );
    }
    const adjacentRow = layout.rows[rowIndex + rowDelta];
    if (!adjacentRow) {
        return undefined;
    }
    if (rowDelta < 0 && adjacentRow.band) {
        return bandCellAt(adjacentRow, colStart);
    }
    const entrySubRow = rowDelta > 0 ? 1 : adjacentRow.rowPercents.length;
    return imageCellsOf(adjacentRow).find((candidate) =>
        covers(candidate, colStart, entrySubRow)
    );
}

/** The band panel under a column, or the band's last one past its width. */
function bandCellAt(row: DatasetRow, column: number): ViewportCell | undefined {
    const width = row.band?.columnPercents.length ?? 0;
    return panelCellsOf(row).find((candidate) =>
        covers(candidate, Math.min(column, width), 1)
    );
}

/** The panes sharing one column track of a row, ordered top to bottom. */
export interface ColumnGroup {
    /** 1-based column track the group occupies. */
    readonly colStart: number;
    readonly cells: ReadonlyArray<ViewportCell>;
}

/**
 * Groups a row's image panes by the column track they start in, so a
 * renderer can lay a row out as one element per column and stack the panes
 * inside it. Groups come left to right, and each group's panes top to
 * bottom. The band's panels are not among them.
 */
export function columnGroups(row: DatasetRow): ColumnGroup[] {
    const byColumn = new Map<number, ViewportCell[]>();
    for (const cell of imageCellsOf(row)) {
        const column = cell.placement.colStart;
        const existing = byColumn.get(column);
        if (existing) {
            existing.push(cell);
        } else {
            byColumn.set(column, [cell]);
        }
    }
    return [...byColumn.entries()]
        .sort(([left], [right]) => left - right)
        .map(([colStart, cells]) => ({
            colStart,
            cells: [...cells].sort(
                (left, right) =>
                    left.placement.rowStart - right.placement.rowStart
            ),
        }));
}
